import { Event } from '../types/event';

/**
 * Utilitaires standardisés pour le comptage des événements
 * Ces fonctions sont utilisées dans toute l'application pour garantir
 * la cohérence des valeurs affichées.
 */

/**
 * Obtient le nombre de sauvegardes pour un événement
 * @param event L'événement dont on souhaite connaître le nombre de sauvegardes
 * @returns Le nombre de sauvegardes
 */
export const getSavedCount = (event: Event): number => {
  // Ordre de priorité défini pour garantir la cohérence:
  // 1. Structure _count (format normalisé de l'API)
  // 2. Propriété directe savedByCount (ancien format)
  // 3. Propriété directe savedCount (format alternatif)
  // 4. Longueur du tableau savedBy (données brutes)
  // 5. Valeur par défaut: 0 (ou 1 si isSaved est true)
  
  if (event._count?.savedBy !== undefined && event._count.savedBy > 0) {
    return event._count.savedBy;
  }
  
  if (event.savedByCount !== undefined) {
    return event.savedByCount;
  }
  
  if (event.savedCount !== undefined) {
    return event.savedCount;
  }
  
  if (event.savedBy?.length !== undefined) {
    return event.savedBy.length;
  }
  
  // Si aucun compteur n'est défini mais que l'utilisateur a sauvegardé l'événement
  // On renvoie au moins 1 (pour éviter d'afficher 0 alors que l'utilisateur a sauvegardé)
  if (event.isSaved === true) {
    return 1;
  }
  
  return 0;
};

/**
 * Obtient le nombre d'intérêts pour un événement
 * @param event L'événement dont on souhaite connaître le nombre d'intérêts
 * @returns Le nombre d'intérêts
 */
export const getInterestCount = (event: Event): number => {
  // Même logique de priorité que getSavedCount
  if (event._count?.interests !== undefined && event._count.interests > 0) {
    return event._count.interests;
  }
  
  if (event.interestedCount !== undefined) {
    return event.interestedCount;
  }
  
  if (event.interests?.length !== undefined) {
    return event.interests.length;
  }
  
  // Si aucun compteur n'est défini mais que l'utilisateur est intéressé par l'événement
  // On renvoie au moins 1 (pour éviter d'afficher 0 alors que l'utilisateur est intéressé)
  if (event.isInterested === true) {
    return 1;
  }
  
  return 0;
};

/**
 * Obtient le nombre de vues pour un événement
 * @param event L'événement dont on souhaite connaître le nombre de vues
 * @returns Le nombre de vues
 */
export const getViewCount = (event: Event): number => {
  // DEBUG: décommenter pour voir toutes les valeurs possibles
  // console.log(`DEBUG getViewCount pour événement ${event.id}:`, {
  //   _count_views: event._count?.views,
  //   viewCount: event.viewCount,
  //   event: event
  // });
  
  // Même logique de priorité que getSavedCount
  if (event._count?.views !== undefined && event._count.views > 0) {
    return event._count.views;
  }
  
  if (event.viewCount !== undefined && event.viewCount > 0) {
    return event.viewCount;
  }
  
  // Vérifier si des propriétés connexes existent qui pourraient indiquer des vues
  if (event.views && typeof event.views === 'number' && event.views > 0) {
    return event.views;
  }
  
  // Si on a un tableau de vues, utiliser sa longueur
  if (Array.isArray(event.views) && event.views.length > 0) {
    return event.views.length;
  }
  
  return 0;
};
